import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Brain,
    Heart,
    User,
    Sparkles,
    ChevronRight,
    ChevronLeft,
    Check,
    Eye,
    Zap,
    Star,
    Target,
    Lightbulb,
    BookOpen,
    Compass,
    Share2,
    Download,
    ArrowRight,
    Camera,
    Search,
    Shield,
    AlertTriangle,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import InputMask from 'react-input-mask';
import Api from '../../services/Api';
import logoSmall from '../../assets/logo-small.png';
import { resolveProfile, getFilteredIrisImages, QUIZ_VERSION, QUIZ_PROFILES } from './quizProfiles';
import { trackQuizEvent, EVENT_TYPES } from './quizAnalytics';

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
const PROCESSING_MESSAGES = [
    'Analisando respostas...',
    'Identificando padrões...',
    'Calculando perfil predominante...',
    'Preparando resultado...',
];

const LOGO_SRC = logoSmall;

// ─────────────────────────────────────────────────────────────
// ANIMATION VARIANTS
// ─────────────────────────────────────────────────────────────
const slideVariants = {
    enter: (direction) => ({
        x: direction > 0 ? 80 : -80,
        opacity: 0,
    }),
    center: {
        x: 0,
        opacity: 1,
    },
    exit: (direction) => ({
        x: direction < 0 ? 80 : -80,
        opacity: 0,
    }),
};

const waitForCardImages = async (element) => {
    const images = Array.from(element.querySelectorAll('img'));
    await Promise.all(images.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
            img.onload = img.onerror = resolve;
        });
    }));
};

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────

/** Welcome Screen */
const WelcomeStep = ({ onStart }) => (
    <motion.div
        className="quiz-welcome"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <div className="quiz-welcome-icon">
            <Eye size={32} />
        </div>

        <h1>Descubra seu perfil mental, emocional e comportamental</h1>

        <p className="quiz-welcome-subtitle">
            Seu modo de pensar, sentir e agir pode estar relacionado às características únicas da sua íris.
        </p>

        <p className="quiz-welcome-description">
            Realize uma autoavaliação rápida e descubra <strong>gratuitamente</strong> seu perfil predominante.
        </p>

        <div className="quiz-welcome-features">
            <div className="quiz-feature-pill"><Brain size={14} /> Perfil Mental</div>
            <div className="quiz-feature-pill"><Heart size={14} /> Perfil Emocional</div>
            <div className="quiz-feature-pill"><User size={14} /> Comportamental</div>
            <div className="quiz-feature-pill"><Star size={14} /> Seus Pontos Fortes</div>
            <div className="quiz-feature-pill"><Lightbulb size={14} /> Tendências Naturais</div>
            <div className="quiz-feature-pill"><BookOpen size={14} /> Estilo de Aprendizado</div>
            <div className="quiz-feature-pill"><Compass size={14} /> Forma de Agir</div>
            <div className="quiz-feature-pill"><Zap size={14} /> Resultado Imediato</div>
        </div>

        <button
            id="quiz-start-btn"
            className="cf-btn-primary"
            onClick={onStart}
            style={{ width: '100%', justifyContent: 'center', padding: '1rem 1.5rem', fontSize: '1.05rem' }}
        >
            Iniciar Avaliação Gratuita <ChevronRight size={20} />
        </button>
    </motion.div>
);

/** Question 1: Iris Color */
const Q1IrisColor = ({ value, onChange }) => (
    <div className="quiz-question-card">
        <div className="quiz-question-header">
            <span className="quiz-question-badge"><Eye size={12} /> Pergunta 1 de 4</span>
            <h2 className="quiz-question-title">Qual a cor predominante da sua íris?</h2>
        </div>
        <div className="quiz-options">
            {['Azul', 'Verde', 'Castanho Claro', 'Castanho Escuro'].map((color) => (
                <button
                    key={color}
                    className={`quiz-option-btn ${value === color ? 'selected' : ''}`}
                    onClick={() => onChange(color)}
                    id={`quiz-color-${color.toLowerCase().replace(' ', '-')}`}
                >
                    <div className="quiz-option-dot">
                        {value === color && <div className="quiz-option-dot-inner" />}
                    </div>
                    {color}
                </button>
            ))}
        </div>
    </div>
);

/** Question 2: Fiber Pattern */
const Q2IrisModel = ({ value, onChange, irisColor, fiberPattern, pigmentation, rings }) => {
    const images = getFilteredIrisImages(irisColor, fiberPattern, pigmentation, rings);

    return (
        <div className="quiz-question-card">
            <div className="quiz-question-header">
                <span className="quiz-question-badge"><Eye size={12} /> Pergunta 2 de 4</span>
                <h2 className="quiz-question-title">Qual destas imagens mais se parece com a sua íris?</h2>
                <p className="quiz-question-hint">Selecione a imagem que mais se aproxima da sua íris real.</p>
                <p className="quiz-welcome-description"><strong>Dica:</strong> Olhe no espelho com uma lanterna para obter uma melhor visualização.</p>
            </div>
            <div className="quiz-iris-grid">
                {images.map((img) => (
                    <div
                        key={img.id}
                        className={`quiz-iris-card ${value === img.id ? 'selected' : ''}`}
                        onClick={() => onChange(img.id)}
                        id={`quiz-iris-${img.id}`}
                    >
                        <img
                            src={img.src}
                            alt={img.name}
                            className="quiz-iris-card-img"
                            onError={(e) => {
                                e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                                e.target.style.height = '140px';
                            }}
                        />
                        {value === img.id && (
                            <div className="quiz-iris-card-check">
                                <Check size={14} />
                            </div>
                        )}
                        <div className="quiz-iris-card-label">{img.name}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/** Question 3: Pigmentation */
const Q3Pigmentation = ({ value, onChange }) => (
    <div className="quiz-question-card">
        <div className="quiz-question-header">
            <span className="quiz-question-badge"><Eye size={12} /> Pergunta 3 de 4</span>
            <h2 className="quiz-question-title">Você percebe manchas ou pigmentações na íris?</h2>
        </div>
        <div className="quiz-options">
            {['Sim', 'Não', 'Não consigo identificar'].map((opt) => (
                <button
                    key={opt}
                    className={`quiz-option-btn ${value === opt ? 'selected' : ''}`}
                    onClick={() => onChange(opt)}
                    id={`quiz-pig-${opt.toLowerCase().replace(/ /g, '-')}`}
                >
                    <div className="quiz-option-dot">
                        {value === opt && <div className="quiz-option-dot-inner" />}
                    </div>
                    {opt}
                </button>
            ))}
        </div>
    </div>
);

/** Question 4: Rings */
const Q4Rings = ({ value, onChange }) => (
    <div className="quiz-question-card">
        <div className="quiz-question-header">
            <span className="quiz-question-badge"><Eye size={12} /> Pergunta 4 de 4</span>
            <h2 className="quiz-question-title">Você percebe círculos ou anéis ao redor da íris?</h2>
        </div>
        <div className="quiz-options">
            {['Sim', 'Não', 'Não consigo identificar'].map((opt) => (
                <button
                    key={opt}
                    className={`quiz-option-btn ${value === opt ? 'selected' : ''}`}
                    onClick={() => onChange(opt)}
                    id={`quiz-ring-${opt.toLowerCase().replace(/ /g, '-')}`}
                >
                    <div className="quiz-option-dot">
                        {value === opt && <div className="quiz-option-dot-inner" />}
                    </div>
                    {opt}
                </button>
            ))}
        </div>
    </div>
);

/** Question 5: Select reference iris image */
// NOTE: Q2IrisModel component is used directly in the quiz flow instead of the original fiber pattern question.

/** Processing animation */
const ProcessingStep = ({ onComplete }) => {
    const [msgIndex, setMsgIndex] = useState(0);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setMsgIndex((prev) => {
                if (prev < PROCESSING_MESSAGES.length - 1) return prev + 1;
                return prev;
            });
        }, 1000);

        const progressInterval = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) return 100;
                return prev + 2;
            });
        }, 80);

        const timer = setTimeout(() => {
            onComplete();
        }, 4000);

        return () => {
            clearInterval(interval);
            clearInterval(progressInterval);
            clearTimeout(timer);
        };
    }, [onComplete]);

    return (
        <motion.div
            className="quiz-processing"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
        >
            <div className="quiz-processing-spinner" />
            <div className="quiz-processing-dots">
                <div className="quiz-processing-dot" />
                <div className="quiz-processing-dot" />
                <div className="quiz-processing-dot" />
            </div>
            <h2>Processando seu perfil...</h2>
            <div className="quiz-processing-message">{PROCESSING_MESSAGES[msgIndex]}</div>
            <div className="quiz-processing-bar">
                <div className="quiz-processing-bar-fill" style={{ width: `${progress}%` }} />
            </div>
        </motion.div>
    );
};

/** Lead capture form (Step 4) */
const LeadCaptureStep = ({ profile, formData, onChange, onSubmit, isSubmitting, errors }) => (
<>
    <motion.div
        className="quiz-lead-capture"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <div className="quiz-lead-result-preview">
            <span className="quiz-lead-result-badge">
                <Sparkles size={13} /> Perfil Identificado
            </span>
            <p className="quiz-lead-result-text">
                Seu perfil apresenta forte tendência para o arquétipo <strong>{profile?.name || 'Visionário Estratégico'}</strong>.
            </p>
            <p className="quiz-lead-result-hint">
                {profile?.combination || 'Visionário + Adaptável + Comunicador'}
            </p>
        </div>
        </motion.div>
        <motion.div
            className="quiz-lead-capture"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >

        <div className="quiz-lead-benefits-card">
            <h3 className="quiz-lead-benefits-title">
                <Star size={16} /> O que você vai receber agora:
            </h3>
            <ul className="quiz-lead-benefits-list">
                <li><User size={16} /> <span>Perfil combinado (Mental + Emocional + Comportamental)</span></li>
                <li><Star size={16} /> <span>Pontos fortes</span></li>
                <li><Target size={16} /> <span>Tendências naturais</span></li>
                <li><Lightbulb size={16} /> <span>Estilo de aprendizado</span></li>
                <li><Zap size={16} /> <span>Forma de tomada de decisão</span></li>
                <li><Share2 size={16} /> <span>Resultado compartilhável</span></li>
            </ul>
        </div>
    </motion.div>
    <motion.div
        className="quiz-lead-capture"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <p className="quiz-lead-cta">
            Receba <strong>gratuitamente</strong> as definições completas do seu perfil mental, emocional e comportamental.
        </p>

        <div className="quiz-lead-form">
            <div className="quiz-lead-field">
                <label htmlFor="quiz-lead-name">Nome</label>
                <input
                    id="quiz-lead-name"
                    type="text"
                    placeholder="Seu nome completo"
                    value={formData.name}
                    onChange={(e) => onChange('name', e.target.value)}
                    className={errors.name ? 'error' : ''}
                />
                {errors.name && <span className="quiz-field-error">{errors.name}</span>}
            </div>

            <div className="quiz-lead-field">
                <label htmlFor="quiz-lead-email">E-mail</label>
                <input
                    id="quiz-lead-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => onChange('email', e.target.value)}
                    className={errors.email ? 'error' : ''}
                    inputMode="email"
                />
                {errors.email && <span className="quiz-field-error">{errors.email}</span>}
            </div>

            <div className="quiz-lead-field">
                <label htmlFor="quiz-lead-phone">WhatsApp</label>
                <InputMask
                    id="quiz-lead-phone"
                    mask="(99) 99999-9999"
                    placeholder="(00) 00000-0000"
                    value={formData.phone}
                    onChange={(e) => onChange('phone', e.target.value)}
                    inputMode="tel"
                    className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="quiz-field-error">{errors.phone}</span>}
            </div>

            <label className={`quiz-lgpd ${errors.lgpd ? 'error' : ''}`}>
                <input
                    type="checkbox"
                    checked={formData.lgpd}
                    onChange={(e) => onChange('lgpd', e.target.checked)}
                    id="quiz-lgpd-checkbox"
                />
                <span>Concordo em receber meu resultado e comunicações exclusivamente relacionadas ao E-IRIS.</span>
            </label>
            {errors.lgpd && <span className="quiz-field-error" style={{ marginTop: '-4px' }}>{errors.lgpd}</span>}
        </div>

        <button
            id="quiz-submit-lead"
            className="cf-btn-primary"
            onClick={onSubmit}
            disabled={isSubmitting}
            style={{ width: '100%', justifyContent: 'center', padding: '1rem 1.5rem', fontSize: '1.05rem' }}
        >
            {isSubmitting ? 'Enviando...' : 'Ver Meu Resultado Completo'}
            {!isSubmitting && <ChevronRight size={20} />}
        </button>
    </motion.div>
</>);

/** Full Result Display (Step 5) */
const ResultStep = ({ profile, onShare, onDownload, onAnalysis, shareCardRef, shareLogoSrc }) => {
    if (!profile) return null;

    return (
        <motion.div
            className="quiz-result"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            {/* Hero */}
            <div className="quiz-result-hero">
                <div className="quiz-result-hero-icon">
                    <Sparkles size={28} />
                </div>
                <h1>Seu Perfil E-IRIS</h1>
                <div className="quiz-result-combination">
                    <Zap size={16} /> {profile.combination}
                </div>
            </div>

            {/* Archetype cards */}
            <div className="quiz-result-archetypes">
                <div className="quiz-result-archetype">
                    <div className="quiz-result-section-icon cerebral"><Brain size={20} /></div>
                    <p className="quiz-result-section-title">Perfil Mental</p>
                    <p className="quiz-result-section-value">{profile.cerebralProfile}</p>
                </div>
                <div className="quiz-result-archetype">
                    <div className="quiz-result-section-icon emotional"><Heart size={20} /></div>
                    <p className="quiz-result-section-title">Perfil Emocional</p>
                    <p className="quiz-result-section-value">{profile.emotionalProfile}</p>
                </div>
                <div className="quiz-result-archetype">
                    <div className="quiz-result-section-icon behavioral"><User size={20} /></div>
                    <p className="quiz-result-section-title">Comportamental</p>
                    <p className="quiz-result-section-value">{profile.behaviorProfile}</p>
                </div>
            </div>

            {/* Strengths */}
            <div className="quiz-result-section">
                <div className="quiz-result-section-header">
                    <div className="quiz-result-section-icon strengths"><Star size={20} /></div>
                    <div>
                        <p className="quiz-result-section-title">Pontos Fortes</p>
                    </div>
                </div>
                <ul className="quiz-result-list">
                    {profile.strengths.map((s, i) => (
                        <li key={i}><Check size={14} /> {s}</li>
                    ))}
                </ul>
            </div>

            {/* Tendencies */}
            <div className="quiz-result-section">
                <div className="quiz-result-section-header">
                    <div className="quiz-result-section-icon tendencies"><Target size={20} /></div>
                    <div>
                        <p className="quiz-result-section-title">Tendências Naturais</p>
                    </div>
                </div>
                <ul className="quiz-result-list">
                    {profile.tendencies.map((t, i) => (
                        <li key={i}><Compass size={14} /> {t}</li>
                    ))}
                </ul>
            </div>

            {/* Learning Style */}
            <div className="quiz-result-section">
                <div className="quiz-result-section-header">
                    <div className="quiz-result-section-icon learning"><BookOpen size={20} /></div>
                    <div>
                        <p className="quiz-result-section-title">Estilo de Aprendizado</p>
                    </div>
                </div>
                <ul className="quiz-result-list">
                    {profile.learningStyle.map((l, i) => (
                        <li key={i}><Lightbulb size={14} /> {l}</li>
                    ))}
                </ul>
            </div>

            {/* Decision Style */}
            <div className="quiz-result-section">
                <div className="quiz-result-section-header">
                    <div className="quiz-result-section-icon decision"><Zap size={20} /></div>
                    <div>
                        <p className="quiz-result-section-title">Forma de Tomada de Decisão</p>
                    </div>
                </div>
                <ul className="quiz-result-list">
                    {profile.decisionStyle.map((d, i) => (
                        <li key={i}><Target size={14} /> {d}</li>
                    ))}
                </ul>
            </div>

            {/* Shareable Card */}
            <div className="quiz-share-section">
                <h3><Share2 size={16} /> Compartilhe seu resultado</h3>
                <div className="quiz-share-canvas-wrap" ref={shareCardRef}>
                    <div className="quiz-share-card" id="quiz-share-card-content">
                        <span className="quiz-share-card-title">Meu Perfil</span>
                        <p style={{ fontSize: '12px', color: '#ffffff50', marginBottom: -5, marginTop: 0 }}>MENTAL + EMOCIONAL + COMPORTAMENTAL</p>
                        <span className="quiz-share-card-combo">{profile.combination}</span>
                        <div className="quiz-share-card-traits">
                            {profile.strengths.slice(0, 5).map((s, i) => (
                                <div key={i} className="quiz-share-card-trait">
                                    <Star size={13} /> {s}
                                </div>
                            ))}
                        </div>
                        <img src={shareLogoSrc} alt="E-IRIS" className="quiz-share-card-logo" crossOrigin="anonymous" />
                        <span className="quiz-share-card-url">analysis.eiris.app/descubra-seu-perfil</span>
                    </div>
                </div>
                <div className="quiz-share-buttons">
                    <button className="quiz-share-btn primary" onClick={onShare} id="quiz-share-btn">
                        <Share2 size={16} /> Compartilhar
                    </button>
                    <button className="quiz-share-btn secondary" onClick={onDownload} id="quiz-download-btn">
                        <Download size={16} /> Baixar Imagem
                    </button>
                </div>
            </div>

            {/* CTA: Full Analysis */}
            <div className="quiz-cta-analysis">
                <h2>Você sabia que a sua íris pode revelar muito além do que apenas características do seu perfil?</h2>
                <p>
                    Sua íris é um mapa, por meio dos sinais presentes nela é possível identificar predisposições e desequilíbrios no seu organismo.
                    Cada região da íris corresponde a diferentes órgãos e sistemas do corpo.
                </p>
                <p>
                    Através de fotografias simples, nossa <strong>Inteligência Artificial</strong> consegue identificar detalhes que normalmente não são
                    perceptíveis durante uma observação comum.
                </p>

                <div className="quiz-cta-comparison">
                    <div className="quiz-cta-compare-item basic">
                        <div className="quiz-cta-compare-item-icon"><AlertTriangle size={18} /></div>
                        <span className="quiz-cta-compare-label">Autoavaliação</span>
                        <span className="quiz-cta-compare-value">Precisão Limitada</span>
                    </div>
                    <div className="quiz-cta-compare-arrow">
                        <ArrowRight size={20} />
                    </div>
                    <div className="quiz-cta-compare-item advanced">
                        <div className="quiz-cta-compare-item-icon"><Camera size={18} /></div>
                        <span className="quiz-cta-compare-label">Foto + IA</span>
                        <span className="quiz-cta-compare-value">Precisão Avançada</span>
                    </div>
                </div>

                <p className="quiz-cta-analysis-note">
                    Este resultado que você obteve foi gerado a partir da sua autoavaliação visual. A maioria das pessoas
                    possui dificuldade para identificar corretamente as características da própria íris. Conte com a nossa IA para uma avaliação completa!
                </p>

                <button
                    id="quiz-analysis-cta"
                    className="cf-btn-primary"
                    onClick={onAnalysis}
                    style={{ width: '100%', justifyContent: 'center', padding: '1.1rem 1.5rem', fontSize: '1.05rem' }}
                >
                    Fazer Análise com IA <ArrowRight size={20} />
                </button>
            </div>
        </motion.div>
    );
};

// ─────────────────────────────────────────────────────────────
// MAIN QUIZ COMPONENT
// ─────────────────────────────────────────────────────────────
const Quiz = ({ onLeadSubmit, isSubmitting }) => {
    const { leadId } = useParams();

    // Steps: welcome, q1, q2, q3, q4, processing, leadCapture, result
    const [currentStep, setCurrentStep] = useState('welcome');
    const [direction, setDirection] = useState(1);
    const [isLoadingLead, setIsLoadingLead] = useState(false);
    const [leadNotFound, setLeadNotFound] = useState(false);

    // Quiz answers
    const [answers, setAnswers] = useState({
        irisColor: '',
        fiberPattern: '',
        pigmentation: '',
        rings: '',
        selectedIrisModel: '',
    });

    // Lead form
    const [leadForm, setLeadForm] = useState({
        name: '',
        phone: '',
        email: '',
        lgpd: false,
    });
    const [leadErrors, setLeadErrors] = useState({});

    // Resolved profile
    const [profile, setProfile] = useState(null);

    // Share card ref
    const shareCardRef = useRef(null);
    const [shareLogoSrc, setShareLogoSrc] = useState(LOGO_SRC);

    // Track quiz start
    const handleStart = useCallback(() => {
        trackQuizEvent(EVENT_TYPES.QUIZ_STARTED, { quizVersion: QUIZ_VERSION });
        setDirection(1);
        setCurrentStep('q1');
    }, []);

    // Track step completion and advance
    const advanceQuestion = useCallback((step, field, value) => {
        setAnswers((prev) => ({ ...prev, [field]: value }));
        trackQuizEvent(EVENT_TYPES.QUIZ_STEP_COMPLETED, {
            step,
            field,
            value,
            quizVersion: QUIZ_VERSION,
        });
        setDirection(1);

        const stepOrder = ['q1', 'q2', 'q3', 'q4'];
        const currentIdx = stepOrder.indexOf(step);
        if (currentIdx < stepOrder.length - 1) {
            setCurrentStep(stepOrder[currentIdx + 1]);
        } else {
            setCurrentStep('processing');
        }
    }, []);

    // Go back
    const goBack = useCallback((fromStep) => {
        setDirection(-1);
        const stepOrder = ['welcome', 'q1', 'q2', 'q3', 'q4'];
        const currentIdx = stepOrder.indexOf(fromStep);
        if (currentIdx > 0) {
            setCurrentStep(stepOrder[currentIdx - 1]);
        }
    }, []);

    // Processing complete
    const handleProcessingComplete = useCallback(() => {
        const resolvedProfile = resolveProfile(answers);
        setProfile(resolvedProfile);
        setDirection(1);
        setCurrentStep('leadCapture');
    }, [answers]);

    // Lead form change
    const handleLeadChange = useCallback((field, value) => {
        setLeadForm((prev) => ({ ...prev, [field]: value }));
        setLeadErrors((prev) => ({ ...prev, [field]: '' }));
    }, []);

    // Lead form validation & submit
    const handleLeadSubmit = useCallback(() => {
        const errors = {};
        if (!leadForm.name.trim()) errors.name = 'Por favor, informe seu nome.';
        if (!leadForm.phone.trim()) errors.phone = 'Informe seu WhatsApp.';
        if (!leadForm.email.trim() || !/\S+@\S+\.\S+/.test(leadForm.email)) {
            errors.email = 'Informe um e-mail válido.';
        }
        if (!leadForm.lgpd) errors.lgpd = 'Você precisa aceitar para continuar.';

        if (Object.keys(errors).length > 0) {
            setLeadErrors(errors);
            return;
        }

        trackQuizEvent(EVENT_TYPES.QUIZ_LEAD_CAPTURED, {
            quizVersion: QUIZ_VERSION,
            profileId: profile?.id,
        });

        // Build full data for parent
        const leadData = {
            ...leadForm,
            ...answers,
            assignedProfile: profile?.id,
            cerebralProfile: profile?.cerebralProfile,
            emotionalProfile: profile?.emotionalProfile,
            behaviorProfile: profile?.behaviorProfile,
            quizVersion: QUIZ_VERSION,
            quizCompleted: true,
        };

        if (onLeadSubmit) {
            onLeadSubmit(leadData, () => {
                setDirection(1);
                setCurrentStep('result');
                trackQuizEvent(EVENT_TYPES.QUIZ_RESULT_VIEWED, {
                    profileId: profile?.id,
                    profileName: profile?.name,
                });
            });
        } else {
            setDirection(1);
            setCurrentStep('result');
            trackQuizEvent(EVENT_TYPES.QUIZ_RESULT_VIEWED, {
                profileId: profile?.id,
                profileName: profile?.name,
            });
        }

    }, [leadForm, answers, profile, onLeadSubmit]);

    // Download share card as image
    const handleDownload = useCallback(async () => {
        trackQuizEvent(EVENT_TYPES.QUIZ_SHARED, {
            method: 'download',
            profileId: profile?.id,
        });

        const cardElement = document.getElementById('quiz-share-card-content');
        if (cardElement) {
            try {
                await waitForCardImages(cardElement);
                const canvas = await html2canvas(cardElement, {
                    backgroundColor: '#0d1b2a',
                    scale: 2,
                    useCORS: true,
                    imageTimeout: 15000,
                });
                const link = document.createElement('a');
                link.download = 'meu-perfil-eiris.png';
                link.href = canvas.toDataURL('image/png');
                link.click();
            } catch (err) {
                console.error('Download error:', err);
            }
        }
    }, [profile]);

    // Share
    const handleShare = useCallback(async () => {
        const shareTitle = 'Meu Perfil E-IRIS';
        const shareText = `Descobri meu perfil: ${profile?.combination || 'Perfil E-IRIS'}! Descubra o seu também.`;
        const shareUrl = 'https://analysis.eiris.app/descubra-seu-perfil';
        const shareData = {
            title: shareTitle,
            text: shareText,
            url: shareUrl,
        };

        const trackShare = (method) => {
            trackQuizEvent(EVENT_TYPES.QUIZ_SHARED, {
                method,
                profileId: profile?.id,
            });
        };

        if (navigator.share) {
            const cardElement = document.getElementById('quiz-share-card-content');
            let shared = false;

            if (cardElement) {
                try {
                    await waitForCardImages(cardElement);
                    const canvas = await html2canvas(cardElement, {
                        backgroundColor: '#0d1b2a',
                        scale: 2,
                        useCORS: true,
                        imageTimeout: 15000,
                    });
                    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

                    if (blob) {
                        const file = new File([blob], 'meu-perfil-eiris.png', { type: 'image/png' });
                        if (navigator.canShare && navigator.canShare({ files: [file] })) {
                            await navigator.share({ ...shareData, files: [file] });
                            trackShare('webShareImage');
                            shared = true;
                        }
                    }
                } catch (err) {
                    console.warn('Falha ao compartilhar imagem, tentando fallback de texto:', err);
                }
            }

            if (!shared) {
                try {
                    await navigator.share(shareData);
                    trackShare('webShare');
                    return;
                } catch (err) {
                    console.warn('Falha ao compartilhar texto/url:', err);
                }
            }
        }

        if (navigator.clipboard && window.isSecureContext) {
            try {
                await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
                trackShare('clipboard');
            } catch (err) {
                console.warn('Falha ao copiar link para a área de transferência:', err);
            }
        }

        handleDownload();
    }, [profile, handleDownload]);

    // Navigate to full analysis
    const handleAnalysis = useCallback(() => {
        trackQuizEvent(EVENT_TYPES.QUIZ_ANALYSIS_CLICKED, {
            profileId: profile?.id,
        });
        // Navigate to request-analysis
        const base = window.location.pathname.split('/descubra-seu-perfil')[0];
        window.location.href = `${base}/request-analysis`;
    }, [profile]);

    // Calculate progress
    const getProgress = () => {
        const steps = { welcome: 0, q1: 1, q2: 2, q3: 3, q4: 4, processing: 5, leadCapture: 6, result: 7 };
        return ((steps[currentStep] || 0) / 6) * 100;
    };

    // Load a lead by id or a profile id if the quiz is accessed through a direct link.
    useEffect(() => {
        if (!leadId) return;

        // If the param matches a known profile id, load it directly
        if (QUIZ_PROFILES && QUIZ_PROFILES[leadId]) {
            setIsLoadingLead(true);
            setLeadNotFound(false);
            const prof = QUIZ_PROFILES[leadId];
            setProfile(prof);
            setCurrentStep('result');
            setIsLoadingLead(false);
            return;
        }

        const loadLead = async () => {
            setIsLoadingLead(true);
            setLeadNotFound(false);
            try {
                const response = await Api.get(`/quiz/lead/${leadId}`);
                if (response && response.success && response.lead) {
                    const lead = response.lead;
                    setAnswers({
                        irisColor: lead.irisColor || '',
                        fiberPattern: lead.fiberPattern || '',
                        pigmentation: lead.pigmentation || '',
                        rings: lead.rings || '',
                        selectedIrisModel: lead.selectedIrisModel || '',
                    });
                    const resolvedProfile = resolveProfile({
                        irisColor: lead.irisColor,
                        fiberPattern: lead.fiberPattern,
                        pigmentation: lead.pigmentation,
                        rings: lead.rings,
                        selectedIrisModel: lead.selectedIrisModel,
                    });
                    setProfile(resolvedProfile);
                    setCurrentStep('result');
                } else {
                    setLeadNotFound(true);
                }
            } catch (error) {
                console.error('Falha ao carregar lead:', error);
                setLeadNotFound(true);
            } finally {
                setIsLoadingLead(false);
            }
        };

        loadLead();
    }, [leadId]);

    // Track abandonment
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (currentStep !== 'welcome' && currentStep !== 'result') {
                trackQuizEvent(EVENT_TYPES.QUIZ_ABANDONED, {
                    abandonedAt: currentStep,
                    quizVersion: QUIZ_VERSION,
                });
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [currentStep]);

    // ─────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────
    const renderCurrentStep = () => {
        if (isLoadingLead) {
            return (
                <motion.div key="loading" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} style={{textAlign: 'center', marginTop: '30%'}}>
                    <div className="quiz-loading">Carregando seu resultado...</div>
                </motion.div>
            );
        }

        if (leadId && leadNotFound) {
            return (
                <motion.div key="notfound" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} style={{textAlign: 'center', marginTop: '30%'}}>
                    <div className="quiz-loading">Resultado não encontrado. Por favor, tente novamente ou inicie o quiz.</div>
                    <button className="cf-btn-primary" onClick={() => { setLeadNotFound(false); setCurrentStep('welcome'); location.href = './'; }} style={{ marginTop: '1.5rem', display: 'initial' }}>
                        Iniciar Quiz
                    </button>
                </motion.div>
            );
        }

        switch (currentStep) {
            case 'welcome':
                return <WelcomeStep key="welcome" onStart={handleStart} />;

            case 'q1':
                return (
                    <motion.div key="q1" custom={direction} variants={slideVariants}
                        initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                        <Q1IrisColor value={answers.irisColor} onChange={(v) => advanceQuestion('q1', 'irisColor', v)} />
                        <div className="quiz-nav" style={{ marginTop: '1rem' }}>
                            <button className="cf-btn-ghost" onClick={() => goBack('q1')}>
                                <ChevronLeft size={16} /> Voltar
                            </button>
                        </div>
                    </motion.div>
                );

            case 'q2':
                return (
                    <motion.div key="q2" custom={direction} variants={slideVariants}
                        initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                        <Q2IrisModel
                            value={answers.selectedIrisModel}
                            onChange={(v) => advanceQuestion('q2', 'selectedIrisModel', v)}
                            irisColor={answers.irisColor}
                            fiberPattern={answers.fiberPattern}
                            pigmentation={answers.pigmentation}
                            rings={answers.rings}
                        />
                        <div className="quiz-nav" style={{ marginTop: '1rem' }}>
                            <button className="cf-btn-ghost" onClick={() => goBack('q2')}>
                                <ChevronLeft size={16} /> Voltar
                            </button>
                        </div>
                    </motion.div>
                );

            case 'q3':
                return (
                    <motion.div key="q3" custom={direction} variants={slideVariants}
                        initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                        <Q3Pigmentation value={answers.pigmentation} onChange={(v) => advanceQuestion('q3', 'pigmentation', v)} />
                        <div className="quiz-nav" style={{ marginTop: '1rem' }}>
                            <button className="cf-btn-ghost" onClick={() => goBack('q3')}>
                                <ChevronLeft size={16} /> Voltar
                            </button>
                        </div>
                    </motion.div>
                );

            case 'q4':
                return (
                    <motion.div key="q4" custom={direction} variants={slideVariants}
                        initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                        <Q4Rings value={answers.rings} onChange={(v) => advanceQuestion('q4', 'rings', v)} />
                        <div className="quiz-nav" style={{ marginTop: '1rem' }}>
                            <button className="cf-btn-ghost" onClick={() => goBack('q4')}>
                                <ChevronLeft size={16} /> Voltar
                            </button>
                        </div>
                    </motion.div>
                );


            case 'processing':
                return <ProcessingStep key="processing" onComplete={handleProcessingComplete} />;

            case 'leadCapture':
                return (
                    <LeadCaptureStep
                        key="leadCapture"
                        profile={profile}
                        formData={leadForm}
                        onChange={handleLeadChange}
                        onSubmit={handleLeadSubmit}
                        isSubmitting={isSubmitting}
                        errors={leadErrors}
                    />
                );

            case 'result':
                return (
                    <ResultStep
                        key="result"
                        profile={profile}
                        onShare={handleShare}
                        onDownload={handleDownload}
                        onAnalysis={handleAnalysis}
                        shareCardRef={shareCardRef}
                        shareLogoSrc={shareLogoSrc}
                    />
                );

            default:
                if (isLoadingLead) {
                    return (
                        <motion.div key="loading" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                            <div className="quiz-loading">Carregando seu resultado...</div>
                        </motion.div>
                    );
                }

                if (leadNotFound) {
                    return (
                        <motion.div key="notfound" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                            <div className="quiz-loading">Resultado não encontrado. Por favor, tente novamente ou inicie o quiz.</div>
                        </motion.div>
                    );
                }

                return null;
        }
    };

    return (
        <div className="quiz-container" id="quiz-page">
            <div className="quiz-inner">
                {/* Progress bar (hide on welcome and result) */}
                {currentStep !== 'welcome' && currentStep !== 'result' && (
                    <div className="quiz-progress-wrap">
                        <div className="quiz-progress-bar">
                            <div className="quiz-progress-fill" style={{ width: `${getProgress()}%` }} />
                        </div>
                        <div className="quiz-progress-label">
                            {currentStep === 'processing' ? 'Processando...' :
                                currentStep === 'leadCapture' ? 'Quase lá!' :
                                    `Etapa ${['q1', 'q2', 'q3', 'q4'].indexOf(currentStep) + 1} de 4`
                            }
                        </div>
                    </div>
                )}

                {/* Step content */}
                <AnimatePresence mode="wait" custom={direction}>
                    {renderCurrentStep()}
                </AnimatePresence>
                {/* Logo */}
                <div className="quiz-logo-wrap">
                    <img src={shareLogoSrc} alt="E-IRIS" className="quiz-logo" />
                </div>
            </div>
        </div>
    );
};

export default Quiz;
